import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Language, LanguageService } from '../../services/language.service';
import { SpaceConfigOptionsService } from '../../services/space-config-options.service';
import { CountryInfo } from '../../models/space.model';

export interface SettingsData {
  countryCode: string;
  languageCode: string;
  currencyCode: string;
}

export interface CurrencyOption {
  code: string;
  name: string;
}

@Component({
  selector: 'app-settings-modal',
  imports: [CommonModule, TranslateModule],
  templateUrl: './settings-modal.component.html',
  styleUrl: './settings-modal.component.scss'
})
export class SettingsModalComponent implements OnChanges {
  @Input() visible = false;
  @Input() initialSettings: SettingsData | null = null;
  @Output() saved = new EventEmitter<SettingsData>();
  @Output() cancelled = new EventEmitter<void>();

  private readonly languageService = inject(LanguageService);
  private readonly spaceConfigOptions = inject(SpaceConfigOptionsService);

  availableLanguages: Language[] = this.languageService.availableLanguages;
  openDropdown: 'country' | 'language' | 'currency' | null = null;

  get countries(): CountryInfo[] {
    return this.spaceConfigOptions.countries;
  }

  currencies: CurrencyOption[] = [
    { code: 'EUR', name: 'Euro' },
    { code: 'USD', name: 'US Dollar' },
    { code: 'GBP', name: 'British Pound' },
    { code: 'CHF', name: 'Swiss Franc' },
    { code: 'SEK', name: 'Swedish Krona' },
    { code: 'NOK', name: 'Norwegian Krone' },
    { code: 'DKK', name: 'Danish Krone' },
    { code: 'PLN', name: 'Polish Zloty' },
    { code: 'CZK', name: 'Czech Koruna' },
  ];

  selectedCountryCode = 'DE';
  selectedLanguageCode = 'en';
  selectedCurrencyCode = 'EUR';

  ngOnChanges(changes: SimpleChanges): void {
    // Only (re-)initialise selections when the modal transitions from closed → open,
    // NOT on every CD cycle while already open, so user edits are not overwritten.
    const becomingVisible = changes['visible']?.currentValue === true
                         && changes['visible']?.previousValue !== true;
    const settingsReplaced = changes['initialSettings'] && !this.visible;

    if ((becomingVisible || settingsReplaced) && this.initialSettings) {
      this.selectedCountryCode = this.initialSettings.countryCode;
      this.selectedLanguageCode = this.initialSettings.languageCode;
      this.selectedCurrencyCode = this.initialSettings.currencyCode;
    }
  }

  get selectedCountryName(): string {
    return this.spaceConfigOptions.countries.find(c => c.code === this.selectedCountryCode)?.name ?? '';
  }

  get selectedLanguageName(): string {
    return this.availableLanguages.find(l => l.code === this.selectedLanguageCode)?.name ?? '';
  }

  get selectedCurrencyName(): string {
    return this.currencies.find(c => c.code === this.selectedCurrencyCode)?.name ?? '';
  }

  toggleDropdown(field: 'country' | 'language' | 'currency'): void {
    this.openDropdown = this.openDropdown === field ? null : field;
  }

  selectCountry(code: string): void {
    this.selectedCountryCode = code;
    this.openDropdown = null;
  }

  selectLanguage(code: string): void {
    this.selectedLanguageCode = code;
    this.openDropdown = null;
  }

  selectCurrency(code: string): void {
    this.selectedCurrencyCode = code;
    this.openDropdown = null;
  }

  closeOpenDropdown(): void {
    this.openDropdown = null;
  }

  save(): void {
    this.saved.emit({
      countryCode: this.selectedCountryCode,
      languageCode: this.selectedLanguageCode,
      currencyCode: this.selectedCurrencyCode
    });
  }

  cancel(): void {
    this.cancelled.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-backdrop')) {
      this.cancel();
    }
  }
}
